import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

const LobbySocketContext = createContext(null);

export const useLobbySocket = () => {
  const context = useContext(LobbySocketContext);
  if (!context) {
    throw new Error('useLobbySocket must be used within a LobbySocketProvider');
  }
  return context;
};

export const LobbySocketProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const isUnmountedRef = useRef(false);
  const connectFnRef = useRef(null);
  
  // Message handlers that can be registered by components
  const messageHandlersRef = useRef(new Set());
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Register a message handler
  const addMessageHandler = useCallback((handler) => {
    messageHandlersRef.current.add(handler);
    return () => {
      messageHandlersRef.current.delete(handler);
    };
  }, []);
  
  // Send message via WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    logger.warn('⚠️ Lobby WebSocket not connected, cannot send message');
    return false;
  }, []);
  
  // Store the current token and isAuthenticated in refs for the connect function
  const tokenRef = useRef(token);
  const isAuthenticatedRef = useRef(isAuthenticated);
  
  useEffect(() => {
    tokenRef.current = token;
    isAuthenticatedRef.current = isAuthenticated;
  }, [token, isAuthenticated]);
  
  // Connect function stored in ref to avoid dependency cycle
  useEffect(() => {
    connectFnRef.current = () => {
      if (!tokenRef.current || !isAuthenticatedRef.current || isUnmountedRef.current) {
        return;
      }
      
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      const wsUrl = `${API_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ws/lobby`;
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        
        ws.onopen = () => {
          if (isUnmountedRef.current) {
            ws.close();
            return;
          }
          setConnected(true);
          reconnectAttempts.current = 0;
          
          // Authenticate immediately
          ws.send(JSON.stringify({
            type: 'join_lobby',
            token: tokenRef.current
          }));
        };
        
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Handle server version announcements instantly so a deploy
            // propagates to all active users within ~1s. We dispatch a global
            // event so the version-monitor consumer (which knows whether a
            // game is in progress) decides how to react — never forces a
            // reload here directly.
            if (data && data.type === 'server_version' && data.version) {
              try {
                window.dispatchEvent(new CustomEvent('chessKingdomServerVersion', {
                  detail: { version: data.version }
                }));
              } catch (e) { /* ignore */ }
            }
            // Notify all registered handlers
            messageHandlersRef.current.forEach(handler => {
              try {
                handler(data);
              } catch (e) {
                logger.error('Error in message handler:', e);
              }
            });
          } catch (e) {
            logger.error('Error parsing lobby message:', e);
          }
        };
        
        ws.onerror = (error) => {
          logger.error('❌ Lobby WebSocket error:', error);
        };
        
        ws.onclose = (event) => {
          setConnected(false);
          wsRef.current = null;
          
          // Attempt reconnection if not unmounted
          if (!isUnmountedRef.current && reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
            reconnectTimeoutRef.current = setTimeout(() => {
              if (!isUnmountedRef.current && connectFnRef.current) {
                connectFnRef.current();
              }
            }, delay);
          }
        };
      } catch (error) {
        logger.error('❌ Failed to create Lobby WebSocket:', error);
      }
    };
  }, [API_URL]);
  
  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setConnected(false);
  }, []);
  
  // Reconnect function (for manual reconnection)
  const reconnect = useCallback(() => {
    if (connectFnRef.current) {
      reconnectAttempts.current = 0;
      connectFnRef.current();
    }
  }, []);
  
  // Connect when authenticated, disconnect when not
  useEffect(() => {
    isUnmountedRef.current = false;
    
    if (isAuthenticated && token && connectFnRef.current) {
      connectFnRef.current();
    }
    
    return () => {
      isUnmountedRef.current = true;
      // Cleanup on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isAuthenticated, token]);
  
  // Handle unauthenticated state separately
  useEffect(() => {
    if (!isAuthenticated || !token) {
      // Close connection when user logs out
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setConnected(false);
    }
  }, [isAuthenticated, token]);
  
  const value = {
    connected,
    sendMessage,
    addMessageHandler,
    reconnect
  };
  
  return (
    <LobbySocketContext.Provider value={value}>
      {children}
    </LobbySocketContext.Provider>
  );
};

export default LobbySocketContext;
